import React from "react";
import { ExportOutlined } from "@ant-design/icons";
import { useVariableValue } from "@devcycle/react-client-sdk";
import styled from "@emotion/styled";
import classNames from "classnames";
import { Flex } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import SrcDocsLink from "components/actions/SrcDocsLink";
import { Currency } from "components/app_layout/Typography/Numbers";
import BulkSaveNoticeBuyer from "components/buyers/BulkSaveNoticeBuyer";
import { SaveBuyerButton } from "components/buyers/SaveBuyerButton";
import {
  convertNoticeFiltersToFrameworkFilters,
  frameworkDetailsTarget,
} from "components/framework/utils";
import { NoticeFilters } from "components/notices/utils";
import BulkSaveNoticeSupplier from "components/suppliers/BulkSaveNoticeSupplier";
import { SaveSupplierButton } from "components/suppliers/SaveSupplierButton";
import { appRoutes } from "lib/appRoutes";
import { NewRelevanceScore } from "lib/core_components/NewRelevanceScore";
import { PropertyInline } from "lib/core_components/Property";
import { RELEVANCY_SEARCH_NEW_USERS } from "lib/featureFlags";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventDataTypes } from "lib/tracking";
import { FrameworkProcessType } from "lib/types/graphQLEnums";
import { SignalEntityType } from "lib/utils/signalUtils";
import NoticeDuplicatesProperty, { Duplicate } from "./NoticeDuplicatesProperty";
import OrganisationsContainer from "./OrganisationsPopover";
import SourceNoticesProperty, { SourceNotice } from "./SourceNoticesProperty";

import css from "./DetailsCardContent.module.scss";

type Supplier = {
  id: number;
  name: string;
  guid: string;
};

type Lot = {
  suppliers?: Supplier[] | null;
};

type DetailsCardProps = {
  notice: {
    guid: string;
    stage: string;
    value: number | null;
    currency: string;
    url: string;
    duplicates: Duplicate[];
    sourceNotices?: SourceNotice[];
  };
  buyers: {
    id: number;
    guid: string;
    name: string;
  }[];
  filters?: NoticeFilters;
  framework?: NoticeDetails["relatedFrameworkProcess"];
  lots: Lot[];
  score: number;
  relationshipBuyerGuid?: string | null;
};

type Org = { id: number; name: string; guid: string };

function getSuppliers(lots: Lot[]): Org[] {
  const suppliers: Supplier[] = [];

  const uniqueSupplierIds = new Set();

  for (const lot of lots) {
    lot.suppliers?.forEach((supplier) => {
      if (!uniqueSupplierIds.has(supplier.id)) {
        suppliers.push(supplier);
        uniqueSupplierIds.add(supplier.id);
      }
    });
  }

  return suppliers;
}

// customised to get the ellipsis to work correctly alongside the icon
function CustomSourcesProperty({ recordUrl }: { recordUrl: string }): JSX.Element {
  return (
    <div className={css.sourceProperty}>
      <span className={css.sourcePropertyTitle}>Sources</span>
      <span className={classNames({ [css.linkEllipsis]: recordUrl })}>
        {recordUrl ? <SrcDocsLink url={recordUrl} linkType="text" showIcon={false} /> : "--"}
      </span>
      {recordUrl && <ExportOutlined className={css.exportIcon} />}
    </div>
  );
}

export default function DetailsCardContent({
  notice,
  buyers,
  score,
  lots,
  filters,
  framework,
  relationshipBuyerGuid,
}: DetailsCardProps) {
  const { checkSubscription: onUnauthorisedClick, authorised } = useCheckSubscription("SUPPLIERS");
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const suppliers = getSuppliers(lots);

  return (
    <div className={css.container}>
      {!isRelevancySearchEnabled && (
        <PropertyInline
          title="Signal score"
          value={
            <NewRelevanceScore
              entityType={SignalEntityType.ProcurementNotice}
              id={notice.guid}
              relevanceScore={score}
              popoverAlignment="center"
              eventAttributes={{
                "Notice guid": notice.guid,
                Stage: notice.stage,
                "Signal score": score,
                "Data type": EventDataTypes.record,
              }}
            />
          }
          className={css.property}
          borderBottom
        />
      )}
      <PropertyInline
        title="Buyer(s)"
        value={
          <>
            <OrganisationsContainer orgs={buyers} orgType="buyers" />
            {buyers.length > 0 && (
              <>
                {buyers.length > 1 ? (
                  <BulkSaveNoticeBuyer buyerGuids={buyers.map((b) => b.guid)} />
                ) : (
                  <SaveBuyerButton
                    buyerGuids={[buyers[0].guid]}
                    trackingData={{ "Context source": "Notice preview" }}
                    iconOnly
                  />
                )}
              </>
            )}
          </>
        }
        className={css.property}
        valueClassName={css.advancedProp}
        borderBottom
      />
      {notice.stage === "AWARDED" && (
        <PropertyInline
          title="Supplier(s)"
          value={
            <>
              <OrganisationsContainer
                orgs={suppliers}
                orgType="suppliers"
                onUnauthorisedClick={onUnauthorisedClick}
                authorised={authorised}
                relationshipBuyerGuid={relationshipBuyerGuid}
              />
              {suppliers.length > 0 && (
                <>
                  {suppliers.length > 1 ? (
                    <BulkSaveNoticeSupplier supplierGuids={suppliers.map((s) => s.guid)} />
                  ) : (
                    <SaveSupplierButton
                      supplierGuids={[suppliers[0].guid]}
                      trackingData={{ "Context source": "Notice preview" }}
                      iconOnly
                    />
                  )}
                </>
              )}
            </>
          }
          className={css.property}
          valueClassName={css.advancedProp}
          borderBottom
        />
      )}
      <PropertyInline
        title="Contract value"
        value={<Currency value={notice.value} currency={notice.currency || undefined} />}
        className={css.property}
        borderBottom
      />
      {!!framework && (
        <PropertyInline
          title="Framework"
          value={
            <Flex column gap="8px">
              <RouterTextLink
                {...appRoutes.frameworkDetail(framework.frameworkId)}
                search={
                  frameworkDetailsTarget(
                    framework.frameworkId,
                    convertNoticeFiltersToFrameworkFilters(filters),
                  ).search
                }
              >
                {framework.frameworkName}
              </RouterTextLink>
              <FrameworkNote>
                {getFrameworkRelatedAlertMessage(framework.processType)}
              </FrameworkNote>
            </Flex>
          }
          className={css.property}
          borderBottom
        />
      )}
      {notice.sourceNotices && notice.sourceNotices.length > 1 ? (
        <SourceNoticesProperty sourceNotices={notice.sourceNotices} />
      ) : (
        <CustomSourcesProperty recordUrl={notice.url} />
      )}
      {notice.duplicates.length > 0 && <NoticeDuplicatesProperty duplicates={notice.duplicates} />}
    </div>
  );
}

const FrameworkNote = styled.div(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  background: theme.colors.sysBackgroundAlternative,
  borderColor: theme.colors.sysBorderDecorative,
  borderRadius: "2px",
  padding: "8px",
}));

function getFrameworkRelatedAlertMessage(processType?: FrameworkProcessType | null) {
  let message = "This notice is part of a framework process";

  if (processType == FrameworkProcessType.FrameworkCallOff) {
    message = "This notice is a call-off from a framework";
  }

  return message;
}
